import { connectivityIncludes } from './chargerConnectivity.js';
/**
 * Site connectivity and underground installs (Warren, 21 October 2026):
 *
 *   "The job asks once: 'Where are the chargers?' Outdoor, indoor garage above ground, or underground. Underground
 *   installs must be on WiFi. The job asks 'Building WiFi available?' Yes: the crew gets job-site instructions for
 *   connecting each charger during install. No WiFi passwords are stored in EVI. No: add the EVI connectivity kit."
 *
 * The answers map onto the job's existing connectivity field (chargerConnectivity.ts): underground is always 'Site WiFi
 * or ethernet', so the PO tells Autel WiFi and the pre-commissioning sheet says the installer joins the site network.
 * Outdoor and an above-ground garage keep whatever the partner chose (or "Decide later").
 *
 * The kit is three store products in the Site connectivity category, seeded unpublished as placeholders (Warren fills
 * in the product, supplier and price). Until every one of them is priced and active, the kit shows "Price to follow
 * from EVI" on the quote and the job, never stops a submit, and the job cannot be ordered.
 *
 * Nothing here ever holds a WiFi password: the crew types it into the Autel app on site, never into EVI.
 */
export const CHARGER_PLACEMENTS = ['outdoor', 'garage_above_ground', 'underground'];
export const CHARGER_PLACEMENT_LABELS = {
    outdoor: 'Outdoor',
    garage_above_ground: 'Indoor garage, above ground',
    underground: 'Underground',
};
/** Warren's questions, verbatim. */
export const CHARGER_PLACEMENT_QUESTION = 'Where are the chargers?';
export const BUILDING_WIFI_QUESTION = 'Building WiFi available?';
/** What a new job starts with when nobody says (one compact choice on New job, defaulting to outdoor). */
export const DEFAULT_CHARGER_PLACEMENT = 'outdoor';
export function isChargerPlacement(v) {
    return typeof v === 'string' && CHARGER_PLACEMENTS.includes(v);
}
/** The store category (a product tier) the kit's products are stocked in. */
export const SITE_CONNECTIVITY_TIER = 'site_connectivity';
export const SITE_CONNECTIVITY_LABEL = 'Site connectivity';
/** The kit's products by part number, one of each per site (seed: scripts/_lib/storeCatalogSeed.ts). */
export const CONNECTIVITY_KIT_PARTS = ['EVI-SC-WIFI-AP-POE', 'EVI-SC-POE-SWITCH', 'EVI-SC-LTE-ROUTER'];
export const CONNECTIVITY_KIT_NAME = 'EVI connectivity kit';
export const CONNECTIVITY_KIT_SUMMARY = 'A WiFi access point (PoE), a PoE switch and an LTE router with an external antenna, so chargers with no building internet reach EVI.';
export const CONNECTIVITY_KIT_PRICE_TO_FOLLOW = 'Price to follow from EVI';
/** Underground with no building WiFi: the job takes the EVI connectivity kit. */
export function needsConnectivityKit(s) {
    return !!s && s.charger_placement === 'underground' && s.building_wifi === false;
}
/** The job's connectivity after the placement answer: underground forces Site WiFi; otherwise the partner's choice stays. */
export function placementConnectivity(placement, current) {
    return placement === 'underground' ? 'site_network' : current;
}
/**
 * The connectivity a job's chargers are ordered and set up with (Warren, 21 October 2026, default for his confirmation:
 * New job no longer asks, so EVI works it out). Underground is Site WiFi; a choice the partner made stands; otherwise
 * outdoor or an indoor garage above ground is EVI cellular, EVI's standard. Null when the job says nothing (no placement).
 */
export function defaultConnectivity(s) {
    if (s.charger_placement === 'underground')
        return 'site_network';
    if (typeof s.connectivity === 'string' && ['site_network', 'evi_cellular', 'both'].includes(s.connectivity))
        return s.connectivity;
    return s.charger_placement === 'outdoor' || s.charger_placement === 'garage_above_ground' ? 'evi_cellular' : null;
}
/** Whether the crew connects the chargers to a WiFi network on site (the building's, or the kit's). */
export function usesSiteWifi(s) {
    return s.charger_placement === 'underground' || connectivityIncludes(s.connectivity, 'site_network');
}
/** The kit's price from the store catalog rows of its parts. */
export function connectivityKitPrice(rows) {
    const parts = CONNECTIVITY_KIT_PARTS.map(pn => rows.find(r => r.part_number === pn) ?? null);
    const priced = parts.every(p => !!p && p.is_active && Number.isSafeInteger(p.price_cents) && p.price_cents > 0);
    return {
        priced,
        unit_price_cents: priced ? parts.reduce((s, p) => s + p.price_cents, 0) : null,
        items: parts.flatMap(p => (p ? [{ part_number: p.part_number, name: p.name }] : [])),
    };
}
/**
 * Putting an Autel charger on a WiFi network with Autel's own app, from the Autel MaxiCharger AC Wallbox manual's
 * "Operation guide for Autel Config APP" (packages/content manual pages 6 to 8). EVI units arrive pre-programmed for the
 * network, so the server settings are left as they are. The password is typed into the Autel app only.
 * Warren to confirm for the AC Ultra and the DC units: their manuals say WiFi needs no install work but give no app steps.
 */
export const AUTEL_WIFI_STEPS = [
    'Power the charger on at its breaker.',
    'Open the Autel Config app on your phone and sign in.',
    'Scan the QR code on the charger (or its Quick Reference Guide) to read its serial number and PIN, or tap Enter manually.',
    'Tap Connect via Bluetooth and stand near the charger until it connects.',
    'Tap Wi-Fi Connection, choose the network, and type its password in the Autel app. Never type a WiFi password into EVI.',
    'Leave the server settings as they are: the charger was programmed for EV Initiative before it shipped.',
    'Restart the charger at the breaker (off, wait ten seconds, on). Within a few minutes it shows Connected here.',
];
/** Before the chargers join it: the EVI connectivity kit's own WiFi. Warren to confirm against the kit he chooses. */
export const CONNECTIVITY_KIT_STEPS = [
    'Mount the LTE router where it has the best signal, with its external antenna above ground or at the ramp, and power it on.',
    'Run a network cable from the router to the PoE switch, and from the switch to the WiFi access point near the chargers.',
    'Wait until the router shows it is online. The kit\'s WiFi name and password are on its label; they never go into EVI.',
];
/** The basics before the WiFi steps, for any charger that shows no signal after power-up. */
export const NO_SIGNAL_CHECKS = [
    'Check the charger has power: its screen or lights are on and its breaker is on.',
    'Wait two minutes after power-up; a charger can take that long to report in.',
];
//# sourceMappingURL=siteConnectivity.js.map